import React from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface UpgradeCardProps {
  title: string;
  description: string;
  icon: string;
  level: number;
  cost: number;
  onPurchase: () => void;
  onPurchaseBulk: (quantity: number) => void;
  canAfford: boolean;
  effect: string;
  formatNumber: (num: number) => string;
  upgradeType: keyof import('../store/gameStore').GameState['upgrades'];
  coins: number;
  getUpgradeCost: (upgradeType: string, currentLevel: number) => number;
  proceduralTools?: { [key: string]: any };
}

const UpgradeCard: React.FC<UpgradeCardProps> = ({
  title,
  description,
  icon,
  level,
  cost,
  onPurchase,
  onPurchaseBulk,
  canAfford,
  effect,
  formatNumber,
  upgradeType,
  coins,
  getUpgradeCost,
  proceduralTools
}) => {
  const [purchaseMode, setPurchaseMode] = React.useState<1 | 10 | 100 | 'max'>('max');
  
  // Funktion zum Formatieren prozeduraler Effekte
  const formatProceduralEffect = (toolKey: string, currentLevel: number, tool: any) => {
    const baseEffect = tool?.baseEffect || 0;
    const effectType = tool?.effectType || 'unknown';
    const effect = baseEffect * currentLevel;
    const safeEffect = isNaN(effect) ? 0 : effect;
    
    switch (effectType) {
      // Klick-spezifische Effekte
      case 'clickMultiplier':
        return `+${formatNumber(safeEffect * 100)}% Klick-Multiplikator`;
      case 'clickBonus':
        return `+${formatNumber(safeEffect)} Münzen pro Klick`;
      case 'autoClickPower':
        return `+${formatNumber(safeEffect * 100)}% Auto-Click-Power`;
      case 'doubleClickChance':
        return `${formatNumber(safeEffect * 100)}% Chance auf doppelte Klicks`;
      case 'gemChance':
        return `+${formatNumber(safeEffect * 100)}% Edelstein-Chance`;
      case 'clickPower':
        return `+${formatNumber(safeEffect * 100)}% Klick-Power`;
      case 'xpBonus':
        return `+${formatNumber(safeEffect * 100)}% XP pro Klick`;
      case 'totalMultiplier':
        return `+${formatNumber(safeEffect * 100)}% Gesamt-Multiplikator`;
      
      // Allgemeine prozedurale Effekte
      case 'gardenBonus':
        return `+${formatNumber(safeEffect * 100)}% Gartenbereich-Bonus`;
      case 'speedMultiplier':
        return `+${formatNumber(safeEffect * 100)}% Geschwindigkeit`;
      case 'upgradeMultiplier':
        return `+${formatNumber(safeEffect * 100)}% Upgrade-Effektivität`;
      case 'incomeMultiplier':
        return `+${formatNumber(safeEffect * 100)}% Einkommen`;
      case 'globalMultiplier':
        return `+${formatNumber(safeEffect * 100)}% alle Multiplikatoren`;
      case 'coinPerSecond':
        return `+${formatNumber(safeEffect)} Münzen/Sek`;
      case 'companionBonus':
        return `+${formatNumber(safeEffect * 100)}% Begleiter-Bonus`;
      case 'costReduction':
        return `-${formatNumber(safeEffect * 100)}% Kosten`;
      case 'bonusDuration':
        return `+${formatNumber(safeEffect * 100)}% Bonus-Dauer`;
      case 'criticalChance':
        return `+${formatNumber(safeEffect * 100)}% kritische Chance`;
      case 'effectMultiplier':
        return `+${formatNumber(safeEffect * 100)}% alle Effekte`;
      case 'processSpeed':
        return `+${formatNumber(safeEffect * 100)}% Prozess-Geschwindigkeit`;
      case 'multiplierStrength':
        return `+${formatNumber(safeEffect * 100)}% Multiplikator-Stärke`;
      case 'resourceAttraction':
        return `+${formatNumber(safeEffect * 100)}% Ressourcen-Anziehung`;
      case 'systemOptimization':
        return `+${formatNumber(safeEffect * 100)}% System-Optimierung`;
      case 'bonusSync':
        return `+${formatNumber(safeEffect * 100)}% Bonus-Synchronisation`;
      case 'growthSpeed':
        return `+${formatNumber(safeEffect * 100)}% Wachstums-Beschleunigung`;
      case 'efficiency':
        return `+${formatNumber(safeEffect * 100)}% Effizienz`;
      default:
        return `+${formatNumber(safeEffect)}`;
    }
  };
  
  // Berechne Kosten basierend auf Kauf-Modus
  const calculateCosts = () => {
    let quantity = 1;
    
    if (purchaseMode === 10) quantity = 10;
    else if (purchaseMode === 100) quantity = 100;
    else if (purchaseMode === 'max') quantity = 1000; // Praktisches Maximum
    
    if (quantity <= 0) return { cost: 0, quantity: 0, actualQuantity: 0 };
    
    // Berechne Gesamtkosten
    let totalCost = 0;
    let affordableQuantity = 0;
    
    for (let i = 0; i < quantity; i++) {
      const levelCost = getUpgradeCost(String(upgradeType), level + i);
      if (totalCost + levelCost <= coins) {
        totalCost += levelCost;
        affordableQuantity++;
      } else {
        break;
      }
    }
    
    return { cost: totalCost, quantity, actualQuantity: affordableQuantity };
  };

  const { cost: bulkCost, quantity: requestedQuantity, actualQuantity } = calculateCosts();
  const displayCost = purchaseMode === 1 ? cost : bulkCost;
  const displayCanAfford = purchaseMode === 1 ? canAfford : (coins >= bulkCost && actualQuantity > 0);

  const handlePurchase = () => {
    if (purchaseMode === 1) {
      onPurchase();
    } else {
      onPurchaseBulk(actualQuantity);
    }
  };
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`rounded-lg p-2 sm:p-3 border shadow-md ${
        String(upgradeType).startsWith('procedural_') 
          ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-300' 
          : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
      }`}
    >
      <div className="text-center">
        <div className="flex items-center justify-center mb-1">
          <div className="text-xl sm:text-2xl mr-2">
            {String(upgradeType).startsWith('procedural_') ? '✨' : icon}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-xs sm:text-sm leading-tight">{title}</h3>
            <p className="text-xs text-gray-600 leading-tight">{description}</p>
          </div>
        </div>
        
        <div className={`rounded p-1.5 mb-1.5 ${
          String(upgradeType).startsWith('procedural_') 
            ? 'bg-purple-50 border border-purple-200' 
            : 'bg-blue-50'
        }`}>
          <div className={`text-xs font-medium ${
            String(upgradeType).startsWith('procedural_') 
              ? 'text-purple-600' 
              : 'text-blue-600'
          }`}>Level: {level || 0}</div>
          
          {/* Spezielle Anzeige für prozedurale Werkzeuge */}
          {String(upgradeType).startsWith('procedural_') && proceduralTools && proceduralTools[String(upgradeType)] ? (
            <div className="space-y-0.5">
              <div className="text-xs text-purple-700 leading-tight font-medium">
                <strong>Aktuell:</strong> {formatProceduralEffect(String(upgradeType), level, proceduralTools[String(upgradeType)])}
              </div>
              <div className="text-xs text-purple-600 leading-tight">
                <strong>Nächstes:</strong> {formatProceduralEffect(String(upgradeType), level + 1, proceduralTools[String(upgradeType)])}
              </div>
            </div>
          ) : (
            <div className={`text-xs leading-tight ${
              String(upgradeType).startsWith('procedural_') 
                ? 'text-purple-700' 
                : 'text-blue-700'
            }`}>{effect}</div>
          )}
        </div>
        
        <div className="text-xs sm:text-sm font-bold text-yellow-600 mb-1.5">
          💰 {isNaN(displayCost) ? '0' : formatNumber(displayCost)}
        </div>
        
        <div className="space-y-1.5">
          {/* Kauf-Modus Buttons - Kompakter */}
          <div className="grid grid-cols-4 gap-1 mb-1.5">
            {[1, 10, 100, 'max'].map((mode) => (
              <button
                key={mode}
                onClick={() => setPurchaseMode(mode as typeof purchaseMode)}
                className={`px-1.5 py-1 text-xs rounded transition-colors min-h-[32px] ${
                  purchaseMode === mode
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {mode === 'max' ? 'MAX' : `${mode}x`}
              </button>
            ))}
          </div>
          
          {/* Kaufen Button - Kompakter */}
          <button
            onClick={handlePurchase}
            disabled={!displayCanAfford}
            className={`w-full py-2 px-2 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-200 min-h-[36px] ${
              displayCanAfford
                ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {displayCanAfford ? (
              purchaseMode === 1 ? '🛒 Kaufen' : `🛒 ${actualQuantity}x`
            ) : (
              (() => {
                const nextLevelCost = displayCost > 0 ? displayCost : cost;
                const displayMode = purchaseMode === 'max' ? '1x' : `${purchaseMode}x`;
                
                return purchaseMode === 'max' ? `🛒 💰 ${formatNumber(nextLevelCost)}` : `🛒 ${displayMode} 💰 ${formatNumber(nextLevelCost)}`;
              })()
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};



export const UpgradeShop: React.FC = () => {
  const {
    coins,
    upgrades,
    powerUps,
    purchaseUpgrade,
    purchaseUpgradeBulk,
    getUpgradeCost,
    formatNumber,
    proceduralTools,
    playerLevel,
  } = useGameStore();

  // Funktion zum Kaufen aller verfügbaren Werkzeug-Upgrades
  const purchaseAllToolUpgrades = () => {
    let remainingCoins = coins;
    
    // Sammle alle verfügbaren Upgrades mit ihren Kosten
    const availableUpgrades: Array<{
      key: string;
      cost: number;
      currentLevel: number;
    }> = [];
    
    upgradeConfigs.forEach((config) => {
      const currentLevel = upgrades[config.key] || 0;
      const cost = getUpgradeCost(config.key, currentLevel);
      const safeCost = isNaN(cost) ? 0 : cost;
      
      if (remainingCoins >= safeCost) {
        availableUpgrades.push({
          key: config.key,
          cost: safeCost,
          currentLevel: currentLevel
        });
      }
    });
    
    // Sortiere nach Kosten (günstigste zuerst)
    availableUpgrades.sort((a, b) => a.cost - b.cost);
    
    // Kaufe die günstigsten Upgrades zuerst, aber maximal verfügbare für jedes Werkzeug
    availableUpgrades.forEach((upgrade) => {
      if (remainingCoins <= 0) return;
      
      let affordableQuantity = 0;
      let totalCost = 0;
      let currentLevel = upgrade.currentLevel;
      
      // Berechne wie viele Upgrades für dieses Werkzeug gekauft werden können
      for (let i = 0; i < 1000; i++) { // Praktisches Maximum
        const levelCost = getUpgradeCost(upgrade.key, currentLevel + i);
        if (totalCost + levelCost <= remainingCoins) {
          totalCost += levelCost;
          affordableQuantity++;
        } else {
          break;
        }
      }
      
      // Kaufe alle verfügbaren Upgrades für dieses Werkzeug
      if (affordableQuantity > 0) {
        purchaseUpgradeBulk(upgrade.key, affordableQuantity);
        remainingCoins -= totalCost;
      }
    });
  };

  // Berechne die Gesamtkosten für alle verfügbaren Werkzeug-Upgrades
  const calculateTotalCostForAllUpgrades = () => {
    let totalCost = 0;
    let remainingCoins = coins;
    
    // Sammle alle verfügbaren Upgrades mit ihren Kosten
    const availableUpgrades: Array<{
      key: string;
      cost: number;
      currentLevel: number;
    }> = [];
    
    upgradeConfigs.forEach((config) => {
      const currentLevel = upgrades[config.key] || 0;
      const cost = getUpgradeCost(config.key, currentLevel);
      const safeCost = isNaN(cost) ? 0 : cost;
      
      if (remainingCoins >= safeCost) {
        availableUpgrades.push({
          key: config.key,
          cost: safeCost,
          currentLevel: currentLevel
        });
      }
    });
    
    // Sortiere nach Kosten (günstigste zuerst)
    availableUpgrades.sort((a, b) => a.cost - b.cost);
    
    // Berechne die Gesamtkosten für die maximal verfügbaren Upgrades
    availableUpgrades.forEach((upgrade) => {
      if (remainingCoins <= 0) return;
      
      let affordableQuantity = 0;
      let toolTotalCost = 0;
      let currentLevel = upgrade.currentLevel;
      
      // Berechne wie viele Upgrades für dieses Werkzeug gekauft werden können
      for (let i = 0; i < 1000; i++) {
        const levelCost = getUpgradeCost(upgrade.key, currentLevel + i);
        if (toolTotalCost + levelCost <= remainingCoins) {
          toolTotalCost += levelCost;
          affordableQuantity++;
        } else {
          break;
        }
      }
      
      totalCost += toolTotalCost;
      remainingCoins -= toolTotalCost;
    });
    
    return totalCost;
  };

  // Prüfe ob mindestens ein Werkzeug-Upgrade verfügbar ist
  const hasAvailableUpgrades = () => {
    return upgradeConfigs.some((config) => {
      const currentLevel = upgrades[config.key] || 0;
      const cost = getUpgradeCost(config.key, currentLevel);
      const safeCost = isNaN(cost) ? 0 : cost;
      return coins >= safeCost;
    });
  };

  const upgradeConfigs = [
    {
      key: 'fertilizer' as const,
      title: 'Premium Dünger',
      description: 'Verstärkt die Effektivität deiner Klicks',
      icon: '🌿',
      effect: `+${(upgrades.fertilizer * 20)}% Klick-Bonus`,
    },
    {
      key: 'wateringCan' as const,
      title: 'Magische Gießkanne',
      description: 'Erhöht den Münzen-Multiplikator',
      icon: '🪣',
      effect: `+${(upgrades.wateringCan * 15)}% Münzen-Bonus`,
    },
    {
      key: 'magicSeeds' as const,
      title: 'Magische Samen',
      description: 'Mehr Erfahrung bei Gartenrenovierungen',
      icon: '🌱',
      effect: upgrades.magicSeeds > 0 ? `+${upgrades.magicSeeds * 5} XP pro Renovierung` : 'Schaltet XP-Bonus frei',
    },
    {
      key: 'gardenGnome' as const,
      title: 'Garten-Gnom',
      description: 'Zufällige Münz-Boni bei Renovierungen',
      icon: '🧙‍♂️',
      effect: upgrades.gardenGnome > 0 ? '30% Chance auf Bonus' : 'Schaltet Zufallsboni frei',
    },
    {
      key: 'rainMaker' as const,
      title: 'Regenmacher',
      description: 'Automatische Bewässerung für passive Einkommen',
      icon: '🌧️',
      effect: upgrades.rainMaker > 0 ? `+${upgrades.rainMaker * 2} Münzen/Min passiv` : 'Schaltet passives Einkommen frei',
    },
    {
      key: 'composter' as const,
      title: 'Super-Komposter',
      description: 'Wandelt überschüssige Münzen in Edelsteine um',
      icon: '♻️',
      effect: upgrades.composter > 0 ? 'Aktiver Edelstein-Generator' : 'Schaltet Edelstein-Konversion frei',
    },
    {
      key: 'goldDetector' as const,
      title: 'Gold-Detektor',
      description: 'Findet versteckte Münzen beim Klicken',
      icon: '🔍',
      effect: upgrades.goldDetector > 0 ? `${upgrades.goldDetector * 5}% Chance auf Bonus-Münzen` : 'Schaltet Münz-Detektion frei',
    },
    {
      key: 'quantumShovel' as const,
      title: 'Quantum-Schaufel',
      description: 'Jeder Klick wirkt mehrfach',
      icon: '⚡',
      effect: upgrades.quantumShovel > 0 ? `Klicks wirken ${upgrades.quantumShovel + 1}x` : 'Schaltet Mehrfach-Klicks frei',
    },
    {
      key: 'luckCharm' as const,
      title: 'Glücks-Amulett',
      description: 'Erhöht die Chance auf Edelsteine',
      icon: '🔮',
      effect: upgrades.luckCharm > 0 ? `+${upgrades.luckCharm * 2}% Edelstein-Chance` : 'Schaltet Glücks-Bonus frei',
    },
    {
      key: 'autoHarvester' as const,
      title: 'Auto-Ernter',
      description: 'Sammelt automatisch Ressourcen',
      icon: '🤖',
      effect: upgrades.autoHarvester > 0 ? `+${upgrades.autoHarvester * 2} Münzen/Sek` : 'Schaltet Auto-Sammlung frei',
    },
    {
      key: 'timeAccelerator' as const,
      title: 'Zeit-Beschleuniger',
      description: 'Beschleunigt alle Prozesse',
      icon: '⏰',
      effect: upgrades.timeAccelerator > 0 ? `+${upgrades.timeAccelerator * 20}% Geschwindigkeit` : 'Schaltet Zeit-Boost frei',
    },
    {
      key: 'megaMultiplier' as const,
      title: 'Mega-Multiplikator',
      description: 'Verstärkt alle Boni drastisch',
      icon: '💎',
      effect: upgrades.megaMultiplier > 0 ? `+${upgrades.megaMultiplier * 50}% alle Boni` : 'Schaltet Mega-Boost frei',
    },
    {
      key: 'crystalGenerator' as const,
      title: 'Kristall-Generator',
      description: 'Erzeugt automatisch Edelsteine',
      icon: '💠',
      effect: upgrades.crystalGenerator > 0 ? `${(upgrades.crystalGenerator * 0.1).toFixed(1)} Edelsteine/Min` : 'Schaltet Edelstein-Generation frei',
    },
    {
      key: 'gardenBot' as const,
      title: 'Garten-Roboter',
      description: 'Führt automatisch Gartenarbeit aus',
      icon: '🦾',
      effect: upgrades.gardenBot > 0 ? `+${upgrades.gardenBot * 5} Münzen/Sek` : 'Schaltet Roboter-Hilfe frei',
    },
    // Garten-spezifische Werkzeuge
    {
      key: 'plantWhisperer' as const,
      title: 'Pflanzen-Flüsterer',
      description: 'Verstärkt alle Gartenbereiche magisch',
      icon: '🌿',
      effect: upgrades.plantWhisperer > 0 ? `+${upgrades.plantWhisperer * 30}% Gartenbereich-Bonus` : 'Schaltet Pflanzen-Magie frei',
    },
    {
      key: 'beeHive' as const,
      title: 'Bienenstock',
      description: 'Bienen bestäuben für Bonuserträge',
      icon: '🐝',
      effect: upgrades.beeHive > 0 ? `+${(upgrades.beeHive * 2.5).toFixed(1)} Münzen/Sek` : 'Schaltet Bestäubung frei',
    },
    {
      key: 'flowerPower' as const,
      title: 'Blumen-Power',
      description: 'Bunte Blumen bringen Glück und Freude',
      icon: '🌸',
      effect: upgrades.flowerPower > 0 ? `+${upgrades.flowerPower * 15}% Klick-Bonus & Edelstein-Chance` : 'Schaltet Blumen-Magie frei',
    },
    {
      key: 'treeFarm' as const,
      title: 'Baum-Farm',
      description: 'Pflanzt automatisch Bäume für Erträge',
      icon: '🌳',
      effect: upgrades.treeFarm > 0 ? `+${upgrades.treeFarm * 3} Münzen/Sek` : 'Schaltet Baum-Plantage frei',
    },
    {
      key: 'herbGarden' as const,
      title: 'Kräuter-Garten',
      description: 'Heilkräuter für magische Boni',
      icon: '🌱',
      effect: upgrades.herbGarden > 0 ? `+${(upgrades.herbGarden * 1.5).toFixed(1)} Münzen/Sek` : 'Schaltet Kräuter-Magie frei',
    },
    {
      key: 'butterflyGarden' as const,
      title: 'Schmetterlings-Garten',
      description: 'Schmetterlinge für Schönheit und Glück',
      icon: '🦋',
      effect: upgrades.butterflyGarden > 0 ? `+${upgrades.butterflyGarden * 12}% Klick-Bonus & Edelstein-Chance` : 'Schaltet Schmetterlings-Magie frei',
    },
    {
      key: 'rockGarden' as const,
      title: 'Stein-Garten',
      description: 'Zen-Steine für Ruhe und Stabilität',
      icon: '🪨',
      effect: upgrades.rockGarden > 0 ? `+${upgrades.rockGarden * 8}% Klick-Stabilität` : 'Schaltet Zen-Ruhe frei',
    },
    {
      key: 'waterFeature' as const,
      title: 'Wasser-Spiel',
      description: 'Beruhigende Wasserspiele',
      icon: '💧',
      effect: upgrades.waterFeature > 0 ? `+${upgrades.waterFeature} Münzen/Sek` : 'Schaltet Wasser-Entspannung frei',
    },
    {
      key: 'seedVault' as const,
      title: 'Samen-Tresor',
      description: 'Lagert seltene und magische Samen',
      icon: '🏦',
      effect: upgrades.seedVault > 0 ? 'Seltene Samen-Sammlung aktiv' : 'Schaltet Samen-Lagerung frei',
    },
    {
      key: 'soilAnalyzer' as const,
      title: 'Boden-Analyzer',
      description: 'Optimiert Bodenqualität wissenschaftlich',
      icon: '🔬',
      effect: upgrades.soilAnalyzer > 0 ? `+${upgrades.soilAnalyzer} Bonus zu allen Gartenbereichen` : 'Schaltet Boden-Optimierung frei',
    },
    {
      key: 'weatherStation' as const,
      title: 'Wetter-Station',
      description: 'Kontrolliert Wetter für optimale Bedingungen',
      icon: '🌤️',
      effect: upgrades.weatherStation > 0 ? `20% Chance auf +${upgrades.weatherStation * 10}% Wetter-Bonus` : 'Schaltet Wetter-Kontrolle frei',
    },
    {
      key: 'gardenLighting' as const,
      title: 'Garten-Beleuchtung',
      description: 'Magische Beleuchtung für Nachtarbeit',
      icon: '💡',
      effect: upgrades.gardenLighting > 0 ? `10% Chance auf +${upgrades.gardenLighting * 20}% Nacht-Bonus` : 'Schaltet Nacht-Gärtnerei frei',
    },
    // Prozedurale Werkzeuge (nur normale prozedurale Werkzeuge, nicht Klick-Werkzeuge)
    ...Object.keys(proceduralTools).map(toolKey => {
      const tool = proceduralTools[toolKey];
      const level = upgrades[toolKey] || 0;
      return {
        key: toolKey as any,
        title: tool.name,
        description: tool.description,
        icon: '🔧', // Standard-Icon für prozedurale Werkzeuge
        effect: level > 0 ? tool.effect : 'Schaltet prozedurale Kräfte frei',
      };
    }),
  ];



  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
      {/* Werkzeug-Upgrades */}
      <div className="mb-3 sm:mb-6">
        <h3 className="text-sm sm:text-lg font-semibold mb-2 sm:mb-3 text-center">🔧 Werkzeug-Upgrades</h3>
        
        {/* Kaufe alle Werkzeug-Upgrades Button - immer sichtbar, aber unterschiedlich dargestellt */}
        <div className="mb-3 sm:mb-4">
          <motion.button
            whileHover={playerLevel >= 1000 && hasAvailableUpgrades() ? { scale: 1.03, y: -2 } : {}}
            whileTap={playerLevel >= 1000 && hasAvailableUpgrades() ? { scale: 0.97 } : {}}
            onClick={playerLevel >= 1000 && hasAvailableUpgrades() ? purchaseAllToolUpgrades : undefined}
            disabled={playerLevel < 1000 || !hasAvailableUpgrades()}
            className={`w-full font-bold py-3 px-6 rounded-xl shadow-2xl transition-all duration-300 text-xs sm:text-sm relative overflow-hidden group ${
              playerLevel >= 1000 && hasAvailableUpgrades()
                ? 'bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-600 hover:to-emerald-700 text-white hover:shadow-3xl cursor-pointer'
                : 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 text-gray-600 cursor-not-allowed opacity-60'
            }`}
          >
            {/* Hintergrund-Animation - nur wenn verfügbar */}
            {playerLevel >= 1000 && hasAvailableUpgrades() && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            )}
            
            {/* Icon und Text */}
            <div className="relative z-10 flex items-center justify-center space-x-1 sm:space-x-2">
              <span className={`text-lg sm:text-xl ${playerLevel >= 1000 && hasAvailableUpgrades() ? 'animate-pulse' : ''}`}>
                {playerLevel >= 1000 ? '🛒' : '🔒'}
              </span>
              <span>
                {playerLevel >= 1000 
                  ? (hasAvailableUpgrades() ? 'Kaufe alle verfügbaren Werkzeug-Upgrades' : 'Keine Upgrades verfügbar')
                  : 'Verfügbar ab Level 1000'
                }
              </span>
              <span className="text-base sm:text-lg">
                {playerLevel >= 1000 && hasAvailableUpgrades() ? '⚡' : '🔒'}
              </span>
            </div>
            
            {/* Kosten-Anzeige - nur wenn verfügbar */}
            {playerLevel >= 1000 && hasAvailableUpgrades() && (
              <div className="relative z-10 text-xs mt-2 opacity-90 bg-black/20 rounded-lg px-3 py-1 inline-block">
                💰 Kosten: {formatNumber(calculateTotalCostForAllUpgrades())} Münzen
              </div>
            )}
            
            {/* Level-Badge */}
            <div className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-full shadow-lg ${
              playerLevel >= 1000 
                ? 'bg-yellow-500 text-black' 
                : 'bg-gray-500 text-white'
            }`}>
              Level {playerLevel}+ 
            </div>
          </motion.button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
          {upgradeConfigs.map((config) => {
            const currentLevel = upgrades[config.key] || 0;
            const cost = getUpgradeCost(config.key, currentLevel);
            const safeCost = isNaN(cost) ? 0 : cost;
            
            return (
              <UpgradeCard
                key={config.key}
                title={config.title}
                description={config.description}
                icon={config.icon}
                level={currentLevel}
                cost={safeCost}
                effect={config.effect}
                canAfford={coins >= safeCost}
                onPurchase={() => purchaseUpgrade(config.key)}
                onPurchaseBulk={(quantity) => purchaseUpgradeBulk(config.key, quantity)}
                formatNumber={formatNumber}
                upgradeType={config.key}
                coins={coins}
                getUpgradeCost={getUpgradeCost}
                proceduralTools={proceduralTools}
              />
            );
          })}
        </div>
      </div>

    </div>
  );
}; 